import {
  Box,
  VStack,
  FormControl,
  FormLabel,
  Text,
  HStack,
  IconButton,
  Select,
  Input,
  InputGroup,
  InputLeftElement,
  CheckboxGroup,
  Checkbox,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  Spinner,
  Center,
} from '@chakra-ui/react';
import { useState, useMemo, useCallback } from 'react';
import { MdAdd, MdSearch, MdDelete } from 'react-icons/md';
import { useDebounce } from '@/client/hooks/useDebounce';
import { TaskType, ApprovalContact, ApprovalLevelUI, ApprovalTemplateBuilderUI } from '@/lib/sdk/chatbot/types';
import { ApprovalTemplateBuilderSection } from './ApprovalTemplateBuilderSection';

const BUILDER_OPTION = { value: 'builder', label: 'Custom (build your own)', description: 'Compose your own header and body variables — no fixed set of body params' };

const RESOLVER_OPTIONS: Record<string, { value: string; label: string; description: string }[]> = {
  [TaskType.DOCUMENT]: [
    { value: 'default', label: 'Default', description: '2 body params: name, task' },
    { value: 'document_forward', label: 'Document Forward', description: 'Forwards the uploaded document as a header so approvers can view it inline' },
  ],
  [TaskType.NUMBER]: [
    { value: 'default', label: 'Default', description: '2 body params: name, task' },
    { value: 'number_with_value', label: 'Number with Value', description: '3 body params: name, task, submitted value' },
    { value: 'number_with_image', label: 'Number with Image', description: 'Generated image header + 3 body params (requires image generator service)' },
  ],
};

// A team alert has no task-type-specific shapes the way maker-checker resolvers do — its
// "default" is the team-alert resolver (image header + sender name), not the 2-param maker
// default (see resolverName fallback in team_alert.go).
const ALERT_RESOLVER_OPTIONS = [
  { value: 'default', label: 'Default', description: "Forwards the firing task's stored image as a header, with the sender's name as the body param" },
];

export interface TeamMemberOption {
  value: string;
  label: string;
  name: string;
  phone: string;
}

interface ApprovalLevelCardProps {
  level: ApprovalLevelUI;
  /** 0-based position for keying/testids; display uses level.level (1-based). */
  index: number;
  /** How many levels are configured in total — a lone level hides its own label and the
   * per-level quorum framing ("this level"), reading identically to today's single-approver UI. */
  totalLevels: number;
  onChange: (level: ApprovalLevelUI) => void;
  onRemove?: () => void;
  taskType: string;
  teamMemberOptions: TeamMemberOption[];
  teamMembersLoading: boolean;
  /** Sibling tasks in this chatbot, for the builder resolver's task-reference pickers. */
  availableTasks: { id: string; name: string }[];
  /**
   * 'alert' renders the trimmed team-alert body — contacts and template only, matching the
   * one-way notification config exactly as it existed before levels: no resolver, no quorum,
   * no level label. A team alert stays single-level (docs/maker-checker-design.md §7.3).
   */
  variant?: 'full' | 'alert';
}

export const ApprovalLevelCard = ({
  level,
  index,
  totalLevels,
  onChange,
  onRemove,
  taskType,
  teamMemberOptions,
  teamMembersLoading,
  availableTasks,
  variant = 'full',
}: ApprovalLevelCardProps) => {
  const isAlert = variant === 'alert';
  const [newContactName, setNewContactName] = useState('');
  const [newContactPhone, setNewContactPhone] = useState('');
  const [teamSearchTerm, setTeamSearchTerm] = useState('');
  const debouncedTeamSearch = useDebounce(teamSearchTerm, 300);

  const contacts = useMemo(() => level.approvalContacts ?? [], [level.approvalContacts]);
  const resolverOptions = [
    ...(isAlert ? ALERT_RESOLVER_OPTIONS : RESOLVER_OPTIONS[taskType] ?? [{ value: 'default', label: 'Default', description: '2 body params: name, task' }]),
    BUILDER_OPTION,
  ];
  const selectedResolver = level.approvalResolver || 'default';

  const handleResolverChange = useCallback((resolver: string) => {
    if (resolver === 'builder' && !level.templateBuilder) {
      const defaultBuilder: ApprovalTemplateBuilderUI = {
        body: [{ kind: 'maker_name' }, { kind: 'task_name' }],
      };
      onChange({ ...level, approvalResolver: resolver, templateBuilder: defaultBuilder });
      return;
    }
    onChange({ ...level, approvalResolver: resolver });
  }, [level, onChange]);
  const quorum = level.quorum || 'any';

  const filteredTeamMembers = useMemo(() => {
    if (!debouncedTeamSearch) return teamMemberOptions;
    const search = debouncedTeamSearch.toLowerCase();
    return teamMemberOptions.filter(
      (m) => m.name.toLowerCase().includes(search) || m.phone.includes(search)
    );
  }, [teamMemberOptions, debouncedTeamSearch]);

  const handleAddManualContact = useCallback(() => {
    const name = newContactName.trim();
    const phone = newContactPhone.trim();
    if (!phone) return;
    const updated: ApprovalContact[] = [...contacts, { name: name || phone, phone }];
    onChange({ ...level, approvalContacts: updated });
    setNewContactName('');
    setNewContactPhone('');
  }, [contacts, level, newContactName, newContactPhone, onChange]);

  const handleAddTeamMemberContact = useCallback((memberPhone: string) => {
    const member = teamMemberOptions.find((m) => m.value === memberPhone);
    if (!member) return;
    if (contacts.some((c) => c.phone === memberPhone)) return;
    const updated: ApprovalContact[] = [...contacts, { name: member.name, phone: member.phone }];
    onChange({ ...level, approvalContacts: updated });
  }, [contacts, level, teamMemberOptions, onChange]);

  const handleRemoveContact = useCallback((phone: string) => {
    const updated: ApprovalContact[] = contacts.filter((c) => c.phone !== phone);
    onChange({ ...level, approvalContacts: updated });
  }, [contacts, level, onChange]);

  const handleContactTypeChange = useCallback((contactType: 'list' | 'manual') => {
    onChange({ ...level, contactType, approvalContacts: [] });
  }, [level, onChange]);

  return (
    <Box
      bg="bg-surface"
      p={4}
      rounded="md"
      borderWidth="1px"
      borderColor="border-default"
      data-testid={`wfv4-approval-level-${index}`}
    >
      <VStack spacing={4} align="stretch">
        {!isAlert && totalLevels > 1 && (
          <HStack justify="space-between" align="flex-start">
            <FormControl>
              <FormLabel fontSize="sm" color="text-primary" mb={1}>
                Level {level.level}
              </FormLabel>
              <Input
                placeholder="Label (e.g. Store Manager, Finance)"
                bg="bg-surface"
                borderColor="border-default"
                size="sm"
                value={level.name || ''}
                onChange={(e) => onChange({ ...level, name: e.target.value })}
              />
            </FormControl>
            {onRemove && (
              <IconButton
                icon={<MdDelete />}
                aria-label={`Remove level ${level.level}`}
                size="sm"
                variant="ghost"
                colorScheme="red"
                mt={7}
                onClick={onRemove}
              />
            )}
          </HStack>
        )}

        {/* Contact Type Toggle */}
        <FormControl>
          <FormLabel fontSize="sm" color="text-primary">
            {isAlert ? 'Alert Contacts' : 'Approval Contacts'}
          </FormLabel>
          <CheckboxGroup
            value={[level.contactType || 'manual']}
            onChange={(values) => handleContactTypeChange((values[values.length - 1] || 'manual') as 'list' | 'manual')}
          >
            <HStack spacing={6} mb={3}>
              <Checkbox
                value="list"
                colorScheme="brand"
                borderColor="border-muted"
                sx={{ '[data-checked]': { borderColor: 'brand.500', backgroundColor: 'brand.500' } }}
              >
                From Team
              </Checkbox>
              <Checkbox
                value="manual"
                colorScheme="brand"
                borderColor="border-muted"
                sx={{ '[data-checked]': { borderColor: 'brand.500', backgroundColor: 'brand.500' } }}
              >
                Manual
              </Checkbox>
            </HStack>
          </CheckboxGroup>

          {/* Selected contacts */}
          {contacts.length > 0 && (
            <Wrap mb={3} spacing={2}>
              {contacts.map((contact) => (
                <WrapItem key={contact.phone}>
                  <Tag colorScheme="brand" borderRadius="full">
                    <TagLabel>{contact.name} ({contact.phone})</TagLabel>
                    <TagCloseButton onClick={() => handleRemoveContact(contact.phone)} />
                  </Tag>
                </WrapItem>
              ))}
            </Wrap>
          )}

          {/* Team member selector */}
          {level.contactType === 'list' && (
            <Box>
              <FormLabel fontSize="sm" color="text-secondary">
                Select Team Members
              </FormLabel>
              <InputGroup size="sm" mb={2}>
                <InputLeftElement pointerEvents="none">
                  <MdSearch color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder="Search team members..."
                  value={teamSearchTerm}
                  onChange={(e) => setTeamSearchTerm(e.target.value)}
                  bg="bg-surface"
                />
              </InputGroup>
              <Box
                maxH="200px"
                overflowY="auto"
                borderWidth="1px"
                borderColor="border-default"
                borderRadius="md"
                p={2}
              >
                {teamMembersLoading ? (
                  <Center py={4}>
                    <Spinner size="sm" color="brand.500" />
                  </Center>
                ) : filteredTeamMembers.length === 0 ? (
                  <Text fontSize="sm" color="text-muted" textAlign="center" py={2}>
                    No team members found
                  </Text>
                ) : (
                  <VStack spacing={1} align="stretch">
                    {filteredTeamMembers.map((member) => (
                      <Checkbox
                        key={member.value}
                        isChecked={contacts.some((c) => c.phone === member.phone)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            handleAddTeamMemberContact(member.value);
                          } else {
                            handleRemoveContact(member.phone);
                          }
                        }}
                        colorScheme="brand"
                        borderColor="border-muted"
                        sx={{ '[data-checked]': { borderColor: 'brand.500', backgroundColor: 'brand.500' } }}
                        py={1}
                        px={2}
                        _hover={{ bg: 'bg-muted' }}
                        borderRadius="md"
                      >
                        <VStack align="start" spacing={0}>
                          <Text fontSize="sm" fontWeight="medium">{member.name}</Text>
                          <Text fontSize="xs" color="text-muted">{member.phone}</Text>
                        </VStack>
                      </Checkbox>
                    ))}
                  </VStack>
                )}
              </Box>
            </Box>
          )}

          {/* Manual contact input */}
          {(level.contactType || 'manual') === 'manual' && (
            <Box>
              <FormLabel fontSize="sm" color="text-secondary">
                Add Contact
              </FormLabel>
              <HStack spacing={2}>
                <Input
                  placeholder="Name (optional)"
                  bg="bg-surface"
                  size="sm"
                  value={newContactName}
                  onChange={(e) => setNewContactName(e.target.value)}
                  flex={1}
                />
                <Input
                  placeholder="+1234567890"
                  bg="bg-surface"
                  size="sm"
                  value={newContactPhone}
                  onChange={(e) => setNewContactPhone(e.target.value)}
                  flex={1}
                />
                <IconButton
                  icon={<MdAdd />}
                  aria-label="Add contact"
                  size="sm"
                  onClick={handleAddManualContact}
                  colorScheme="brand"
                  isDisabled={!newContactPhone.trim()}
                />
              </HStack>
            </Box>
          )}
        </FormControl>

        {/* Template */}
        <FormControl>
          <FormLabel fontSize="sm" color="text-primary">
            {isAlert ? 'Alert Template' : 'Approval Template'}
          </FormLabel>
          <Input
            placeholder="Enter WhatsApp template name (e.g. valet_request_message)"
            bg="bg-surface"
            borderColor="border-default"
            size="sm"
            value={level.approvalTemplate || ''}
            onChange={(e) => onChange({ ...level, approvalTemplate: e.target.value })}
          />
        </FormControl>

        {/* Resolver — determines the WhatsApp template shape. Available in both modes: a
            team alert has no quorum, but it still picks how its template components are
            built (issue #711's "wiring the template builder" follow-up). */}
        <FormControl>
          <FormLabel fontSize="sm" color="text-primary">
            {isAlert ? 'Alert Resolver' : 'Approval Resolver'}
          </FormLabel>
          <Select
            bg="bg-surface"
            borderColor="border-default"
            size="sm"
            value={selectedResolver}
            onChange={(e) => handleResolverChange(e.target.value)}
          >
            {resolverOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
          <Text fontSize="xs" color="text-muted" mt={1}>
            {resolverOptions.find((o) => o.value === selectedResolver)?.description}
          </Text>
        </FormControl>

        {selectedResolver === 'builder' && (
          <ApprovalTemplateBuilderSection
            builder={level.templateBuilder || { body: [] }}
            availableTasks={availableTasks}
            onChange={(templateBuilder) => onChange({ ...level, templateBuilder })}
            allowButtons
          />
        )}

        {!isAlert && (
          <FormControl>
            <FormLabel fontSize="sm" color="text-primary">
              How many approvers are required{totalLevels > 1 ? ' at this level' : ''}?
            </FormLabel>
            <Select
              bg="bg-surface"
              borderColor="border-default"
              size="sm"
              value={quorum}
              onChange={(e) => {
                const nextQuorum = e.target.value as 'any' | 'all' | 'm_of_n';
                onChange({
                  ...level,
                  quorum: nextQuorum,
                  quorumCount: nextQuorum === 'm_of_n' ? (level.quorumCount ?? 1) : undefined,
                });
              }}
            >
              <option value="any">Any one approver</option>
              <option value="all">All approvers</option>
              <option value="m_of_n">A specific number of approvers</option>
            </Select>
            {quorum === 'm_of_n' && (
              <Input
                mt={2}
                type="number"
                min={1}
                max={Math.max(contacts.length, 1)}
                size="sm"
                bg="bg-surface"
                borderColor="border-default"
                value={level.quorumCount ?? 1}
                onChange={(e) => onChange({ ...level, quorumCount: Number(e.target.value) })}
              />
            )}
            <Text fontSize="xs" color="text-muted" mt={1}>
              {quorum === 'any' && 'The first approver to respond decides this level — today\'s default behaviour.'}
              {quorum === 'all' && 'Every listed approver must approve before this level clears.'}
              {quorum === 'm_of_n' && `Clears once this many of the ${contacts.length || 0} listed approvers have approved.`}
            </Text>
          </FormControl>
        )}
      </VStack>
    </Box>
  );
};
